// ZONE 1 · NEXT MOVE — ranked, evidence-cited, ADVISORY.
//
// The queue arrives ranked (weight asc, then the page's engagement order, then rule id) and
// every item carries its own reason, its evidence chips and its route. Nothing here decides
// anything: the lead item is a recommendation with its working shown, and the operator may
// ignore it. It is never empty — the terminal states ("Nothing is blocked", "Create an
// engagement") are items too, because a zone that disappears is indistinguishable from a
// zone that broke.
//
// A move NAMES its engagement only when the client has more than one live: with one, naming
// it is noise; with two, an unnamed move is the scope error in miniature.
import { useState } from 'react';
import { dressRefs } from './BriefText.js';
import { useT } from '../i18n/index.js';
import './ClientNextMove.de.js';
import './ClientNextMove.fr.js';
import type { BriefQueueItem, RouteHandler } from '../api/brief.js';

/** The verb each rule's route deserves — a property of the RULE, never of the client. */
const CTA: Record<string, string> = {
  'engagement-absent': 'New engagement',
  'gate-engine-down': 'Open Lifecycle',
  'register-unconfirmed': 'Open the register',
  'commitment-open': 'Open the commitment',
  'proposals-pending': 'Open the review queue',
  'conflicts-open': 'Open the conflicts',
  'facts-unconfirmed': 'Review the facts',
  'questions-open': 'Open the questions',
  'estimate-vs-fact': 'Open Contracting',
  'operator-task': 'Open the task',
  'all-clear': 'Open Lifecycle',
};

const ctaFor = (item: BriefQueueItem) => CTA[item.rule] ?? 'Open';

function EngTag({ item, multi }: { item: BriefQueueItem; multi: boolean }) {
  if (!multi || !item.engagement) return null;
  return <span className="client-qeng src-ref">{item.engagement.code}</span>;
}

function Chips({ item }: { item: BriefQueueItem }) {
  const t = useT();
  if (!item.chips.length && !item.derived) return null;
  return (
    <div className="client-chips">
      {item.chips.map((chip, i) => (
        <span key={`${chip}-${i}`} className="client-chip">{dressRefs(chip)}</span>
      ))}
      {/* Rule 40 is a co-existence check, not a computed inequality — it says so itself. */}
      {item.derived && <span className="client-chip client-chip--derived">{t('derived check')}</span>}
    </div>
  );
}

interface Props {
  queue: BriefQueueItem[];
  /** More than one LIVE engagement — the only condition under which a move names its own. */
  multi: boolean;
  onRoute: RouteHandler;
}

export default function ClientNextMove({ queue, multi, onRoute }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (!queue.length) return null;
  const [lead, ...rest] = queue;

  return (
    <section className="zone" id="nextmove">
      <div className="zone-head">
        <span className="u-label">{t('Next move')}</span>
        <span className="zone-count voice-figure">
          {t('{n} queued', { n: queue.length })}{multi ? ` · ${t('across the live engagements')}` : ''}
        </span>
      </div>

      <div className="sheet sheet--edge client-move">
        <div className="client-move-head">
          <h2><EngTag item={lead} multi={multi} />{lead.title}.</h2>
          {lead.route && (
            <div className="client-move-cta">
              <button type="button" className="btn btn-accent" onClick={() => onRoute(lead.route)}>
                {t(ctaFor(lead))} →
              </button>
            </div>
          )}
        </div>
        <p className="client-move-why read">{dressRefs(lead.why)}</p>
        <Chips item={lead} />
      </div>

      {rest.length > 0 && (
        <div>
          <button type="button" className="client-qtoggle" onClick={() => setOpen((v) => !v)}>
            {open ? '▾' : '▸'} {t('{n} more, ranked', { n: rest.length })}
          </button>
          {open && (
            <div className="sheet sheet--clip">
              {rest.map((item, i) => (
                <div className="client-qrow" key={`${item.rule}-${item.engagement?.id ?? 0}-${i}`}>
                  <div className="client-qn voice-figure">{i + 2}</div>
                  <div className="client-qbody">
                    <div className="client-qtitle"><EngTag item={item} multi={multi} />{item.title}</div>
                    <div className="client-qwhy">{dressRefs(item.why)}</div>
                    <Chips item={item} />
                  </div>
                  {item.route && (
                    <div>
                      <button type="button" className="btn btn-sm" onClick={() => onRoute(item.route)}>
                        {t(ctaFor(item))} →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
